/**
 * 🔄 SmartBoutik - Service de Synchronisation
 * Version corrigée avec gestion des erreurs robuste
 */

import apiCall, { endpoints } from './api';
import offlineDB from './offlineDatabase';

// États de synchronisation
export const SYNC_STATUS = {
  IDLE: 'idle',
  SYNCING: 'syncing',
  SUCCESS: 'success',
  ERROR: 'error',
  OFFLINE: 'offline'
};

// Configuration
const SYNC_CONFIG = {
  AUTO_SYNC_INTERVAL: 5 * 60 * 1000, // 5 minutes
  RETRY_DELAY: 30 * 1000,
  MAX_RETRY_ATTEMPTS: 3,
  BATCH_SIZE: 50
};

// État global du service
let syncState = {
  status: SYNC_STATUS.IDLE,
  lastSync: null,
  pendingCount: 0,
  isOnline: navigator.onLine,
  isDBReady: false,
  listeners: []
};

let autoSyncInterval = null;

/**
 * ✅ Initialiser le service de synchronisation
 */
export const initSyncService = async () => {
  console.log('🔄 Initialisation du service de synchronisation...');
  
  try {
    // Initialiser la base de données (non bloquant)
    const db = await offlineDB.initDatabase();
    syncState.isDBReady = db !== null;
    
    if (syncState.isDBReady) {
      console.log('✅ IndexedDB prête');
      
      // Récupérer la dernière sync
      syncState.lastSync = await offlineDB.getLastSyncTime();
      
      // Compter les éléments en attente
      const queue = await offlineDB.getSyncQueue() || [];
      const pending = await offlineDB.getPendingSales() || [];
      syncState.pendingCount = queue.length + pending.filter(s => s.status === 'pending').length;
    } else {
      console.warn('⚠️ IndexedDB non disponible - Mode dégradé');
    }
    
    // Écouter les changements de connexion
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Mettre à jour l'état initial
    syncState.isOnline = navigator.onLine;
    
    // Démarrer la synchronisation automatique
    startAutoSync();
    
    // Si en ligne et DB prête, synchroniser
    if (syncState.isOnline && syncState.isDBReady) {
      setTimeout(() => syncAll(), 2000);
    }
    
    console.log('✅ Service de synchronisation initialisé');
    notifyListeners();
    
    return syncState;
    
  } catch (error) {
    console.error('❌ Erreur initialisation sync service:', error);
    // Continuer même en cas d'erreur
    syncState.isDBReady = false;
    return syncState;
  }
};

/**
 * Gestion du passage en ligne
 */
const handleOnline = async () => {
  console.log('🌐 Connexion rétablie !');
  syncState.isOnline = true;
  notifyListeners();
  
  if (syncState.isDBReady) {
    setTimeout(() => syncAll(), 1000);
  }
};

/**
 * Gestion du passage hors ligne
 */
const handleOffline = () => {
  console.log('📴 Connexion perdue');
  syncState.isOnline = false;
  syncState.status = SYNC_STATUS.OFFLINE;
  notifyListeners();
};

/**
 * Démarrer la synchronisation automatique
 */
const startAutoSync = () => {
  if (autoSyncInterval) {
    clearInterval(autoSyncInterval);
  }
  
  autoSyncInterval = setInterval(() => {
    if (syncState.isOnline && syncState.isDBReady && syncState.status !== SYNC_STATUS.SYNCING) {
      syncAll();
    }
  }, SYNC_CONFIG.AUTO_SYNC_INTERVAL);
  
  console.log(`⏰ Sync automatique: ${SYNC_CONFIG.AUTO_SYNC_INTERVAL / 1000}s`);
};

/**
 * Arrêter la synchronisation automatique
 */
export const stopAutoSync = () => {
  if (autoSyncInterval) {
    clearInterval(autoSyncInterval);
    autoSyncInterval = null;
  }
};

/**
 * Ajouter un écouteur de changements d'état
 */
export const addSyncListener = (callback) => {
  syncState.listeners.push(callback);
  return () => {
    syncState.listeners = syncState.listeners.filter(cb => cb !== callback);
  };
};

/**
 * Notifier tous les écouteurs
 */
const notifyListeners = () => {
  const state = getSyncState();
  syncState.listeners.forEach(callback => {
    try {
      callback(state);
    } catch (error) {
      console.error('Erreur listener sync:', error);
    }
  });
};

/**
 * Obtenir l'état actuel
 */
export const getSyncState = () => ({
  ...syncState,
  listeners: undefined
});

// ============================================
// 📥 SYNCHRONISATION DESCENDANTE
// ============================================

/**
 * Synchroniser les produits
 */
export const syncProducts = async () => {
  if (!syncState.isOnline) {
    const cached = syncState.isDBReady ? await offlineDB.getProducts() : [];
    return { success: true, source: 'cache', data: cached || [] };
  }
  
  try {
    console.log('📦 Synchronisation des produits...');
    const response = await apiCall.get(endpoints.products.list);
    
    if (response.success && response.data) {
      const products = Array.isArray(response.data) ? response.data : response.data.content || [];
      
      if (syncState.isDBReady) {
        await offlineDB.saveProducts(products);
      }
      
      console.log(`✅ ${products.length} produits synchronisés`);
      return { success: true, source: 'server', data: products };
    }
    
    throw new Error(response.message || 'Erreur récupération produits');
  } catch (error) {
    console.error('❌ Erreur sync produits:', error);
    const cached = syncState.isDBReady ? await offlineDB.getProducts() : [];
    return { success: true, source: 'cache', data: cached || [], error: error.message };
  }
};

/**
 * Synchroniser les catégories
 */
export const syncCategories = async () => {
  if (!syncState.isOnline) {
    const cached = syncState.isDBReady ? await offlineDB.getCategories() : [];
    return { success: true, source: 'cache', data: cached || [] };
  }
  
  try {
    console.log('🏷️ Synchronisation des catégories...');
    const response = await apiCall.get(endpoints.categories.list);
    
    if (response.success && response.data) {
      const categories = Array.isArray(response.data) ? response.data : [];
      
      if (syncState.isDBReady) {
        await offlineDB.saveCategories(categories);
      }
      
      console.log(`✅ ${categories.length} catégories synchronisées`);
      return { success: true, source: 'server', data: categories };
    }
    
    throw new Error(response.message);
  } catch (error) {
    console.error('❌ Erreur sync catégories:', error);
    const cached = syncState.isDBReady ? await offlineDB.getCategories() : [];
    return { success: true, source: 'cache', data: cached || [], error: error.message };
  }
};

/**
 * Synchroniser les clients
 */
export const syncCustomers = async () => {
  if (!syncState.isOnline) {
    const cached = syncState.isDBReady ? await offlineDB.getCustomers() : [];
    return { success: true, source: 'cache', data: cached || [] };
  }
  
  try {
    console.log('👤 Synchronisation des clients...');
    const response = await apiCall.get(endpoints.customers.list);
    
    if (response.success && response.data) {
      const customers = Array.isArray(response.data) ? response.data : response.data.content || [];
      
      if (syncState.isDBReady) {
        await offlineDB.saveCustomers(customers);
      }
      
      console.log(`✅ ${customers.length} clients synchronisés`);
      return { success: true, source: 'server', data: customers };
    }
    
    throw new Error(response.message);
  } catch (error) {
    console.error('❌ Erreur sync clients:', error);
    const cached = syncState.isDBReady ? await offlineDB.getCustomers() : [];
    return { success: true, source: 'cache', data: cached || [], error: error.message };
  }
};

/**
 * Synchroniser l'historique des ventes
 */
export const syncSalesHistory = async (limit = 100) => {
  if (!syncState.isOnline) {
    const cached = syncState.isDBReady ? await offlineDB.getSalesHistory(limit) : [];
    return { success: true, source: 'cache', data: cached || [] };
  }
  
  try {
    console.log('📊 Synchronisation historique ventes...');
    const response = await apiCall.get(endpoints.sales.list, { size: limit });
    
    if (response.success && response.data) {
      const sales = Array.isArray(response.data) ? response.data : response.data.content || [];
      
      if (syncState.isDBReady) {
        await offlineDB.saveSalesHistory(sales);
      }
      
      console.log(`✅ ${sales.length} ventes historiques synchronisées`);
      return { success: true, source: 'server', data: sales };
    }
    
    throw new Error(response.message);
  } catch (error) {
    console.error('❌ Erreur sync historique:', error);
    const cached = syncState.isDBReady ? await offlineDB.getSalesHistory(limit) : [];
    return { success: true, source: 'cache', data: cached || [], error: error.message };
  }
};

// ============================================
// 📤 SYNCHRONISATION MONTANTE
// ============================================

/**
 * Synchroniser les ventes en attente
 * ✅ CORRIGÉ: Format snake_case pour le backend
 */
export const syncPendingSales = async () => {
  if (!syncState.isOnline || !syncState.isDBReady) {
    return { success: false, message: 'Hors ligne ou DB non disponible' };
  }
  
  const pendingSales = await offlineDB.getPendingSales() || [];
  const toSync = pendingSales.filter(s => s.status === 'pending' || s.status === 'error');
  
  if (toSync.length === 0) {
    console.log('✅ Aucune vente en attente');
    return { success: true, synced: 0 };
  }
  
  console.log(`📤 Synchronisation de ${toSync.length} vente(s) en attente...`);
  
  let synced = 0;
  let errors = 0;
  
  for (const sale of toSync) {
    try {
      await offlineDB.updatePendingSaleStatus(sale.localId, 'syncing');
      
      // ✅ CORRECTION: Format snake_case pour le backend
      const saleData = {
        customer_name: sale.customerName || sale.customer?.name || 'Client Anonyme',
        customer_phone: sale.customerPhone || sale.customer?.phone || null,
        payment_method: sale.paymentMethod || 'CASH',
        is_credit: sale.isCredit || false,
        items: (sale.items || []).map(item => ({
          product_id: item.productId,
          quantity: parseInt(item.quantity) || 1,
          unit_price: parseFloat(item.unitPrice || item.price) || 0
        })),
        discount: parseFloat(sale.discount) || 0,
        tax: 0,
        notes: sale.notes || `Vente offline - ${sale.localId}`
      };
      
      console.log('📤 Envoi vente offline au backend:', saleData);
      
      const response = await apiCall.post(endpoints.sales.create, saleData);
      
      if (response.success) {
        await offlineDB.updatePendingSaleStatus(sale.localId, 'synced', response.data?.id);
        setTimeout(() => offlineDB.removeSyncedSale(sale.localId), 5000);
        synced++;
        console.log(`✅ Vente ${sale.localId} synchronisée -> ${response.data?.id || response.data?.saleNumber}`);
      } else {
        throw new Error(response.message || 'Erreur création vente');
      }
    } catch (error) {
      console.error(`❌ Erreur sync vente ${sale.localId}:`, error);
      await offlineDB.updatePendingSaleStatus(sale.localId, 'error');
      errors++;
    }
  }
  
  return { success: errors === 0, synced, errors, total: toSync.length };
};

/**
 * Traiter la file d'attente
 */
export const processSyncQueue = async () => {
  if (!syncState.isOnline || !syncState.isDBReady) {
    return { success: false, message: 'Hors ligne ou DB non disponible' };
  }
  
  const queue = await offlineDB.getSyncQueue() || [];
  
  if (queue.length === 0) {
    return { success: true, processed: 0 };
  }
  
  console.log(`🔄 Traitement de ${queue.length} élément(s)...`);
  
  let processed = 0;
  let errors = 0;
  
  for (const item of queue) {
    try {
      let success = false;
      
      switch (item.type) {
        case 'SALE':
          // Traité par syncPendingSales
          success = true;
          break;
          
        case 'CUSTOMER':
          if (item.action === 'CREATE') {
            const response = await apiCall.post(endpoints.customers.create, item.data);
            success = response.success;
          }
          break;
          
        default:
          success = true;
      }
      
      if (success) {
        await offlineDB.removeFromSyncQueue(item.id);
        processed++;
      }
    } catch (error) {
      console.error('Erreur traitement queue:', error);
      errors++;
    }
  }
  
  return { success: errors === 0, processed, errors };
};

// ============================================
// 🔄 SYNCHRONISATION COMPLÈTE
// ============================================

/**
 * Synchronisation complète
 */
export const syncAll = async () => {
  if (!syncState.isOnline) {
    console.log('📴 Hors ligne');
    syncState.status = SYNC_STATUS.OFFLINE;
    notifyListeners();
    return { success: false, message: 'Hors ligne' };
  }
  
  if (syncState.status === SYNC_STATUS.SYNCING) {
    console.log('⏳ Sync en cours');
    return { success: false, message: 'Sync en cours' };
  }
  
  console.log('🔄 Synchronisation complète...');
  syncState.status = SYNC_STATUS.SYNCING;
  notifyListeners();
  
  const results = {};
  
  try {
    // Montante
    if (syncState.isDBReady) {
      console.log('📤 Synchronisation montante...');
      results.pendingSales = await syncPendingSales();
      results.syncQueue = await processSyncQueue();
    }
    
    // Descendante
    console.log('📥 Synchronisation descendante...');
    results.categories = await syncCategories();
    results.products = await syncProducts();
    results.customers = await syncCustomers();
    
    // Métadonnées
    if (syncState.isDBReady) {
      await offlineDB.setLastSyncTime();
      syncState.lastSync = new Date().toISOString();
      
      const queue = await offlineDB.getSyncQueue() || [];
      const pending = await offlineDB.getPendingSales() || [];
      syncState.pendingCount = queue.length + pending.filter(s => s.status === 'pending').length;
    }
    
    syncState.status = SYNC_STATUS.SUCCESS;
    console.log('✅ Synchronisation complète terminée');
    
  } catch (error) {
    console.error('❌ Erreur synchronisation:', error);
    syncState.status = SYNC_STATUS.ERROR;
  }
  
  notifyListeners();
  return { success: syncState.status === SYNC_STATUS.SUCCESS, results };
};

/**
 * Forcer une synchronisation
 */
export const forceSync = async () => {
  console.log('🔄 Synchronisation forcée');
  return syncAll();
};

// ============================================
// 🛒 HELPERS POUR LES VENTES
// ============================================

/**
 * ✅ Créer une vente (online ou offline)
 */
export const createSale = async (saleData) => {
  // Si en ligne, essayer d'envoyer directement
  if (syncState.isOnline) {
    try {
      console.log('🌐 Tentative création vente en ligne...');
      const response = await apiCall.post(endpoints.sales.create, saleData);
      
      if (response.success) {
        console.log('✅ Vente créée en ligne:', response.data?.id || response.data?.saleNumber);
        
        // Mettre à jour le stock local si DB prête
        if (syncState.isDBReady && saleData.items) {
          for (const item of saleData.items) {
            try {
              const products = await offlineDB.getProducts() || [];
              const product = products.find(p => p.id === item.productId);
              if (product && product.stockQuantity !== undefined) {
                await offlineDB.updateLocalStock(
                  item.productId, 
                  Math.max(0, product.stockQuantity - item.quantity)
                );
              }
            } catch (e) {
              // Ignorer les erreurs de mise à jour stock local
            }
          }
        }
        
        return { success: true, online: true, data: response.data };
      }
      
      throw new Error(response.message || 'Erreur création vente');
    } catch (error) {
      console.warn('⚠️ Échec vente online:', error.message);
      
      // Si DB non prête, propager l'erreur
      if (!syncState.isDBReady) {
        throw error;
      }
      
      // Sinon, continuer en mode offline
    }
  }
  
  // Mode offline
  if (!syncState.isDBReady) {
    throw new Error('Mode offline non disponible (IndexedDB non initialisé)');
  }
  
  console.log('📴 Création vente en mode offline');
  
  // Mettre à jour le stock local
  if (saleData.items) {
    for (const item of saleData.items) {
      try {
        const products = await offlineDB.getProducts() || [];
        const product = products.find(p => p.id === item.productId);
        if (product && product.stockQuantity !== undefined) {
          await offlineDB.updateLocalStock(
            item.productId, 
            Math.max(0, product.stockQuantity - item.quantity)
          );
        }
      } catch (e) {
        // Ignorer
      }
    }
  }
  
  const offlineSale = await offlineDB.createOfflineSale(saleData);
  
  if (!offlineSale) {
    throw new Error('Impossible de créer la vente offline');
  }
  
  // Mettre à jour le compteur
  syncState.pendingCount++;
  notifyListeners();
  
  return { success: true, online: false, data: offlineSale };
};

/**
 * Obtenir les produits
 */
export const getProducts = async () => {
  if (syncState.isOnline) {
    const result = await syncProducts();
    return result.data || [];
  }
  
  if (syncState.isDBReady) {
    return await offlineDB.getProducts() || [];
  }
  
  return [];
};

/**
 * Rechercher produit par code-barres
 */
export const getProductByBarcode = async (barcode) => {
  // D'abord en local
  if (syncState.isDBReady) {
    const localProduct = await offlineDB.getProductByBarcode(barcode);
    if (localProduct) return localProduct;
  }
  
  // Puis sur le serveur
  if (syncState.isOnline) {
    try {
      const response = await apiCall.get(endpoints.employee.products.scanByBarcode(barcode));
      if (response.success && response.data) {
        if (syncState.isDBReady) {
          await offlineDB.saveProducts([response.data]);
        }
        return response.data;
      }
    } catch (error) {
      console.error('Erreur recherche barcode:', error);
    }
  }
  
  return null;
};

/**
 * Obtenir les clients
 */
export const getCustomers = async () => {
  if (syncState.isOnline) {
    const result = await syncCustomers();
    return result.data || [];
  }
  
  if (syncState.isDBReady) {
    return await offlineDB.getCustomers() || [];
  }
  
  return [];
};

/**
 * Obtenir les catégories
 */
export const getCategories = async () => {
  if (syncState.isOnline) {
    const result = await syncCategories();
    return result.data || [];
  }
  
  if (syncState.isDBReady) {
    return await offlineDB.getCategories() || [];
  }
  
  return [];
};

// ============================================
// 📊 STATISTIQUES
// ============================================

export const getSyncStats = async () => {
  const storageStats = syncState.isDBReady ? await offlineDB.getStorageStats() : {};
  const pendingSales = syncState.isDBReady ? await offlineDB.getPendingSales() || [] : [];
  const queue = syncState.isDBReady ? await offlineDB.getSyncQueue() || [] : [];
  
  return {
    ...storageStats,
    pendingSalesCount: pendingSales.filter(s => s.status === 'pending').length,
    errorSalesCount: pendingSales.filter(s => s.status === 'error').length,
    syncQueueCount: queue.length,
    lastSync: syncState.lastSync,
    isOnline: syncState.isOnline,
    isDBReady: syncState.isDBReady,
    status: syncState.status
  };
};

// ============================================
// 🧹 NETTOYAGE
// ============================================

export const cleanupSyncService = () => {
  window.removeEventListener('online', handleOnline);
  window.removeEventListener('offline', handleOffline);
  stopAutoSync();
  syncState.listeners = [];
  console.log('🧹 Service sync nettoyé');
};

export const resetOfflineData = async () => {
  if (syncState.isDBReady) {
    await offlineDB.clearAllData();
  }
  syncState.lastSync = null;
  syncState.pendingCount = 0;
  notifyListeners();
  console.log('🔄 Données offline réinitialisées');
};

export default {
  initSyncService,
  cleanupSyncService,
  getSyncState,
  getSyncStats,
  addSyncListener,
  syncAll,
  forceSync,
  syncProducts,
  syncCategories,
  syncCustomers,
  syncSalesHistory,
  syncPendingSales,
  processSyncQueue,
  createSale,
  getProducts,
  getProductByBarcode,
  getCustomers,
  getCategories,
  resetOfflineData,
  stopAutoSync,
  SYNC_STATUS
};